import type { AnswerWithDiscussion } from "./types"

const sortByLocation = (
  a: AnswerWithDiscussion,
  b: AnswerWithDiscussion,
): number => {
  const aLoc = a.primary_answer.location
  const bLoc = b.primary_answer.location
  if (!aLoc || !bLoc) {
    return 0
  }
  if (aLoc.sheetName !== bLoc.sheetName) {
    return aLoc.sheetName! < bLoc.sheetName! ? -1 : 1
  }

  return aLoc.firstRowIndex > bLoc.firstRowIndex
    ? 1
    : aLoc.firstRowIndex === bLoc.firstRowIndex
      ? 0
      : -1
}

const sortByQuestionInt = (
  a: AnswerWithDiscussion,
  b: AnswerWithDiscussion,
): number => {
  return Number.parseInt(a.question_id!) - Number.parseInt(b.question_id!)
}

const createSortByPriorityFunction = (
  currentUserUid: string,
  defaultSorter: AnswerDiscussionSortFunction,
) => {
  return (a: AnswerWithDiscussion, b: AnswerWithDiscussion): number => {
    const isUserTaggedInA =
      a.discussions?.some(
        (d) => d.assignment?.uid === currentUserUid && !d.resolution,
      ) ?? false

    const isUserTaggedInB =
      b.discussions?.some(
        (d) => d.assignment?.uid === currentUserUid && !d.resolution,
      ) ?? false

    // If one has the user tagged and the other doesn't, prioritize the one with the tag
    if (isUserTaggedInA !== isUserTaggedInB) {
      return isUserTaggedInA ? -1 : 1
    }

    // Show in lexical order:
    //  (no text, edited or reviewed, confidence, document order)
    const hasTextDiff =
      (a.primary_answer.text !== "" ? 1 : 0) -
      (b.primary_answer.text !== "" ? 1 : 0)
    if (hasTextDiff !== 0) {
      return hasTextDiff
    }

    const hasLastReviewedDiff =
      (a.last_reviewed_by ? 1 : 0) - (b.last_reviewed_by ? 1 : 0)
    if (hasLastReviewedDiff !== 0) {
      return hasLastReviewedDiff
    }

    const editedStatusDiff =
      (a.last_edited_by ? 1 : 0) - (b.last_edited_by ? 1 : 0)
    if (editedStatusDiff !== 0) {
      return editedStatusDiff
    }

    const confidenceDiff = a.confidence - b.confidence
    if (confidenceDiff !== 0) {
      return confidenceDiff
    }

    return defaultSorter(a, b)
  }
}

const createSortByConfidenceFunction = (
  defaultSorter: AnswerDiscussionSortFunction,
) => {
  return (a: AnswerWithDiscussion, b: AnswerWithDiscussion): number => {
    const confidenceDiff = a.confidence - b.confidence // low to high
    return confidenceDiff !== 0 ? confidenceDiff : defaultSorter(a, b)
  }
}

type AnswerDiscussionSortFunction = (
  a: AnswerWithDiscussion,
  b: AnswerWithDiscussion,
) => number

interface SorterFunctions {
  priority: AnswerDiscussionSortFunction
  confidence: AnswerDiscussionSortFunction
  default: AnswerDiscussionSortFunction
}

export function getSorters(
  uid: string,
  answers: AnswerWithDiscussion[],
): SorterFunctions {
  // TODO(mgraczyk): Find a way to do this on backend, store something like document order.
  const defaultSorter = answers.every(
    (a) =>
      a.primary_answer.location == null &&
      a.question_id != null &&
      Number.parseInt(a.question_id).toString() === a.question_id,
  )
    ? sortByQuestionInt
    : sortByLocation

  return {
    priority: createSortByPriorityFunction(uid, defaultSorter),
    confidence: createSortByConfidenceFunction(defaultSorter),
    default: defaultSorter,
  }
}
